// S7 — a tenant's usage (spec 15 §4; M15-7a, D-496, D-617). A month picker,
// the month's rollup (MAU, events, peak storage), a 90-day sparkline per
// metric with its latest reading, and a CSV export of the same rows. A day
// the meter never read shows as a gap and an empty CSV cell, never as 0
// (spec 15 §9.14). The AI token figures and their fair-use bar wait on module
// 14 (spec 15 §0.6), so nothing here stands in for them.

import { useState } from "react";
import { keepPreviousData, useQuery } from "@tanstack/react-query";
import { useNavigate } from "@tanstack/react-router";
import { useTranslation } from "react-i18next";
import { PageHeader } from "../../../components/breadcrumbs";
import { Button } from "../../../components/button";
import { Card } from "../../../components/card";
import { Field } from "../../../components/field";
import { Select } from "../../../components/select";
import { CardSkeleton, EmptyState, ErrorState } from "../../../components/states";
import { formatTimestamp } from "../../../lib/datetime";
import { requestIdOf, useSignInAgainOn401 } from "../components/console-frame";
import { Fact, Facts } from "../components/facts";
import { Sparkline } from "../components/sparkline";
import { TenantTabs } from "../components/tenant-tabs";
import { getTenant, PlatformError, type PlatformMe } from "../lib/platform-api";
import { getUsage, type TenantUsage, type UsageMetric } from "../lib/plans-api";
import {
  formatBytes,
  formatCount,
  monthLabel,
  pickerMonths,
  usageCsv,
  utcMonth,
} from "../lib/plans";
import { tenantQueryKey } from "./tenant-detail";

function formatMetric(metric: UsageMetric, value: number, language: string): string {
  return metric === "storage_bytes" ? formatBytes(value, language) : formatCount(value, language);
}

/** Save the export through a short-lived object URL; nothing leaves the browser. */
function download(filename: string, text: string): void {
  const url = URL.createObjectURL(new Blob([text], { type: "text/csv;charset=utf-8" }));
  const a = document.createElement("a");
  a.href = url;
  a.download = filename;
  document.body.append(a);
  a.click();
  a.remove();
  URL.revokeObjectURL(url);
}

export function TenantUsageScreen({ tenantId }: { me: PlatformMe; tenantId: string }) {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const months = pickerMonths(new Date());
  const [month, setMonth] = useState(utcMonth(new Date()));
  const detail = useQuery({
    queryKey: tenantQueryKey(tenantId),
    queryFn: ({ signal }) => getTenant(tenantId, signal),
  });
  const usage = useQuery({
    queryKey: ["platform", "tenant", tenantId, "usage", month],
    queryFn: ({ signal }) => getUsage(tenantId, month, signal),
    placeholderData: keepPreviousData,
  });
  useSignInAgainOn401(detail.error ?? usage.error);
  const crumbs = [{ label: t("admin.tenant.back"), to: "/console/tenants" }];

  if (detail.isPending) {
    return (
      <section className="admin-screen" data-testid="admin-usage-loading">
        <PageHeader title={t("admin.usage.loading")} crumbs={crumbs} />
        <CardSkeleton lines={6} label={t("admin.usage.loading")} />
      </section>
    );
  }
  if (detail.isError) {
    const notFound =
      detail.error instanceof PlatformError &&
      (detail.error.status === 404 || detail.error.status === 400);
    return (
      <section className="admin-screen" data-testid="admin-usage-error">
        <PageHeader
          title={notFound ? t("admin.tenant.notFoundTitle") : t("admin.load.failed")}
          crumbs={crumbs}
        />
        {notFound ? (
          <EmptyState
            title={t("admin.tenant.notFoundTitle")}
            body={t("admin.tenant.notFoundBody")}
            ctaLabel={t("admin.tenant.back")}
            onCta={() => {
              void navigate({ to: "/console/tenants" });
            }}
          />
        ) : (
          <ErrorState
            title={t("admin.load.failed")}
            retryLabel={t("admin.load.retry")}
            onRetry={() => {
              void detail.refetch();
            }}
            requestId={requestIdOf(detail.error)}
            requestIdLabel={t("admin.load.requestId")}
          />
        )}
      </section>
    );
  }

  const tenant = detail.data.tenant;
  let body;
  if (usage.isPending) {
    body = <CardSkeleton lines={6} label={t("admin.usage.loading")} />;
  } else if (usage.isError) {
    body = (
      <ErrorState
        title={t("admin.load.failed")}
        retryLabel={t("admin.load.retry")}
        onRetry={() => {
          void usage.refetch();
        }}
        requestId={requestIdOf(usage.error)}
        requestIdLabel={t("admin.load.requestId")}
      />
    );
  } else {
    body = <UsageBody usage={usage.data} subdomain={tenant.subdomain} />;
  }

  return (
    <section className="admin-screen" data-testid="admin-usage">
      <PageHeader title={tenant.name} crumbs={crumbs} />
      <TenantTabs tenantId={tenantId} />
      <div className="admin-filters">
        <Field label={t("admin.usage.month")}>
          <Select
            value={month}
            data-testid="admin-usage-month"
            onChange={(e) => {
              setMonth(e.target.value);
            }}
          >
            {months.map((m) => (
              <option key={m} value={m}>
                {monthLabel(m, i18n.language)}
              </option>
            ))}
          </Select>
        </Field>
      </div>
      {body}
    </section>
  );
}

function UsageBody({ usage, subdomain }: { usage: TenantUsage; subdomain: string }) {
  const { t, i18n } = useTranslation();
  const measured = usage.series.some((s) => s.points.some((p) => p.value !== null));
  if (!measured && usage.monthly === null) {
    return <EmptyState title={t("admin.usage.emptyTitle")} body={t("admin.usage.emptyBody")} />;
  }
  return (
    <>
      <Card title={t("admin.usage.monthTitle")} data-testid="admin-usage-month-card">
        <Card.Body>
          {usage.monthly === null ? (
            <p className="admin-muted" data-testid="admin-usage-no-rollup">
              {t("admin.usage.noRollup")}
            </p>
          ) : (
            <Facts data-testid="admin-usage-rollup">
              <Fact label={t("admin.usage.mau")} data-testid="admin-usage-mau">
                {formatCount(usage.monthly.mau, i18n.language)}
              </Fact>
              <Fact label={t("admin.usage.domainEvents")}>
                {formatCount(usage.monthly.domain_events, i18n.language)}
              </Fact>
              <Fact label={t("admin.usage.storagePeak")}>
                {usage.monthly.storage_bytes_peak === null
                  ? t("admin.usage.noReading")
                  : formatBytes(usage.monthly.storage_bytes_peak, i18n.language)}
              </Fact>
              <Fact label={t("admin.usage.refreshed")}>
                {formatTimestamp(usage.monthly.refreshed_at, i18n.language)}
              </Fact>
            </Facts>
          )}
        </Card.Body>
      </Card>
      <Card
        title={t("admin.usage.daily")}
        data-testid="admin-usage-daily"
        action={
          <Button
            size="sm"
            data-testid="admin-usage-export"
            onClick={() => {
              download(
                `usage-${subdomain}-${usage.month}.csv`,
                usageCsv(usage, {
                  date: t("admin.usage.csvDate"),
                  metric: (m) => t(`admin.usage.metric.${m}`),
                }),
              );
            }}
          >
            {t("admin.usage.exportCsv")}
          </Button>
        }
      >
        <Card.Body>
          <ul className="admin-spark-list">
            {usage.series.map((s) => {
              const last = [...s.points].reverse().find((p) => p.value !== null);
              const name = t(`admin.usage.metric.${s.metric}`);
              return (
                <li key={s.metric} className="admin-spark-row" data-metric={s.metric}>
                  <span className="admin-spark-name">{name}</span>
                  <Sparkline
                    points={s.points}
                    label={t("admin.usage.sparkLabel", {
                      metric: name,
                      from: usage.from,
                      to: usage.to,
                    })}
                    data-testid={`admin-usage-spark-${s.metric}`}
                  />
                  <span
                    className="admin-spark-latest"
                    data-testid={`admin-usage-latest-${s.metric}`}
                  >
                    {last?.value == null
                      ? t("admin.usage.noReading")
                      : formatMetric(s.metric, last.value, i18n.language)}
                  </span>
                </li>
              );
            })}
          </ul>
          <p className="admin-hint">
            {t("admin.usage.window", { from: usage.from, to: usage.to })}
          </p>
        </Card.Body>
      </Card>
    </>
  );
}
